'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import useSWR from 'swr';
import { useSession } from 'next-auth/react';

// Actions for mutations (user-triggered, not on page load)
import addFavoriteAction from '@/app/_lib/actions/AddFavoriteAction';
import deleteFavoriteAction from '@/app/_lib/actions/DeleteFavoriteAction';

// Stable empty array reference to prevent infinite re-renders
const EMPTY_ARRAY = [];

// Fetcher uses Route Handler (not Server Action) to avoid bot/crawler errors.
// Bots that render JS dispatch Server Actions on mount, causing RSC errors.
// GET requests to Route Handlers don't have this problem.
const favoritesFetcher = async () => {
  const response = await fetch('/api/favorites');
  const data = await response.json();
  if (data.success) {
    return data.favorites;
  }
  return [];
};

/**
 * useFavorites - SWR-based favorites hook
 *
 * Works for both authenticated users and anonymous visitors.
 * Automatically refetches when auth status changes (favorites merge on signin).
 *
 * Returns:
 *   favorites: array - array of sub_product_ids
 *   count: number - total favorites count for badge display
 *   isLoading: boolean - true during initial fetch
 *   isFavorite: function(subProductId) - check if product is favorited
 *   addFavorite: function(subProductId) - add to favorites and refresh
 *   removeFavorite: function(subProductId) - remove from favorites and refresh
 *   refresh: function - manual refresh
 */
export function useFavorites() {
  const $ = _c(16);
  const {
    status: sessionStatus
  } = useSession();
  let t0;
  if ($[0] !== sessionStatus) {
    t0 = sessionStatus === "loading" ? null : ["favorites", sessionStatus];
    $[0] = sessionStatus;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: favorites,
    isLoading,
    mutate
  } = useSWR(t0, favoritesFetcher, t1);
  const favoritesArray = favorites || EMPTY_ARRAY;
  let t2;
  if ($[3] !== favoritesArray) {
    t2 = subProductId => favoritesArray.includes(subProductId);
    $[3] = favoritesArray;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const isFavorite = t2;
  let t3;
  if ($[5] !== mutate) {
    t3 = async (subProductId_0, t4) => {
      const listId = t4 === undefined ? null : t4;
      const response = await addFavoriteAction({
        sub_product_id: subProductId_0,
        list_id: listId
      });
      if (response.success) {
        mutate();
      }
      return response;
    };
    $[5] = mutate;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const addFavorite = t3;
  let t4;
  if ($[7] !== mutate) {
    t4 = async subProductId_1 => {
      const response_0 = await deleteFavoriteAction({
        sub_product_id: subProductId_1
      });
      if (response_0.success) {
        mutate();
      }
      return response_0;
    };
    $[7] = mutate;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const removeFavorite = t4;
  const t5 = isLoading || sessionStatus === "loading";
  let t6;
  if ($[9] !== addFavorite || $[10] !== favoritesArray || $[11] !== isFavorite || $[12] !== mutate || $[13] !== removeFavorite || $[14] !== t5) {
    t6 = {
      favorites: favoritesArray,
      count: favoritesArray.length,
      isLoading: t5,
      isFavorite,
      addFavorite,
      removeFavorite,
      refresh: mutate
    };
    $[9] = addFavorite;
    $[10] = favoritesArray;
    $[11] = isFavorite;
    $[12] = mutate;
    $[13] = removeFavorite;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}